// app/javascript/controllers/generate_pdf_controller.js
import { Controller } from 'stimulus'

export default class extends Controller {
  connect() {
    const ds = this.element.dataset
    this.complaintUrl = ds.generatePdfComplaintUrlValue
    this.uploadUrl = ds.generatePdfUploadUrlValue
    this.documentsUrl = ds.generatePdfDocumentsUrlValue
    this.complaintCode = ds.generatePdfComplaintCodeValue

    console.log('🔧 Init ->', {
      complaintUrl: this.complaintUrl,
      uploadUrl: this.uploadUrl,
      documentsUrl: this.documentsUrl,
      complaintCode: this.complaintCode
    })

    this.generatePdf().catch(err => console.error('💥 PDF flow error:', err))
  }

  async generatePdf() {
    await this._waitForHtml2pdf(6000)

    // 1) Payload oculto
    const payload = document.getElementById('pdf-payload')
    if (!payload) throw new Error('No se encontró #pdf-payload')

    const code = this._meta('pdf-code')
    const institution = this._meta('pdf-institution')
    const dateText =
      this._meta('pdf-date') ||
      new Date().toLocaleDateString('es-PE', { year: 'numeric', month: 'long', day: 'numeric' })
    const personName = this._meta('pdf-person')
    const responseHtml = this._getSavedResponseHtml()

    // 2) HTML autocontenido
    const fullUrl = new URL(this.complaintUrl, window.location.origin).toString()
    const baseHref = window.location.origin
    const innerHtml = `
      <p class="w-full text-right mb-2">${this._esc(dateText)}</p>
      <p class="w-full font-bold">${this._esc(institution)}</p>
      <p>Estimado(a) Sr(a). <strong>${this._esc(personName)}</strong>:</p>
      ${responseHtml || '<p>(sin contenido)</p>'}
      <p class="mt-2"><span class="font-bold">Atentamente</span><br>El Responsable del Libro de Reclamaciones</p>
    `
    const builtHtml = this._buildPdfHtml({ headerUrl: fullUrl, baseHref, complaintCode: code, innerHtml })
    console.log('🧱 [build] HTML length:', builtHtml.length)

    // 3) Generar PDF SIN iframe (desde string). Fallback: DIV offscreen.
    const ts = new Date().toISOString().replace(/[-:.TZ]/g, '')
    const filename = `reclamo_${code}_${ts}.pdf`
    const opt = {
      margin: [0.2, 0.4, 0.4, 0.4],
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, allowTaint: false },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    }

    let pdfBlob
    try {
      console.log('🖨️ [A] html2pdf.from(HTML string)')
      console.time('⏱️ html2pdf (string)')
      pdfBlob = await html2pdf().set(opt).from(builtHtml).outputPdf('blob')
      console.timeEnd('⏱️ html2pdf (string)')
    } catch (e) {
      console.warn('⚠️ from(string) falló, intento fallback DIV:', e)
      const host = this._makeOffscreenDivFromHtml(builtHtml)
      try {
        console.time('⏱️ html2pdf (div)')
        pdfBlob = await html2pdf().set(opt).from(host).outputPdf('blob')
        console.timeEnd('⏱️ html2pdf (div)')
      } finally {
        host.remove()
      }
    }
    if (!pdfBlob) throw new Error('No se pudo generar el PDF')
    console.log('🗂️ PDF Blob size:', pdfBlob.size)

    // 4) Subir y PATCH
    const uploadEndpoint = `${this.uploadUrl}?filename=${filename}`
    const up = await fetch(uploadEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/pdf',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: pdfBlob
    })
    if (!up.ok) throw new Error(`Upload fallo: ${up.status}`)

    const patch = await fetch(this.documentsUrl, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ filename, status: 'uploaded' })
    })
    if (!patch.ok) throw new Error(`PATCH fallo: ${patch.status}`)
    const json = await patch.json().catch(() => ({}))
    this.documentJson = json.document

    // 5) Habilitar botón de firma
    const p = this.element.querySelector('p')
    if (p) p.innerText = '✅ PDF enviado para firma'
    this._enableSignButton(json?.document, filename)

    console.log('🎉 Listo. PDF armado SIN iframe, subido y listo para firma.')
  }

  // ===== Helpers =====
  _waitForHtml2pdf(timeoutMs = 6000) {
    return new Promise((resolve, reject) => {
      const t0 = performance.now()
      const tick = () => {
        if (typeof window.html2pdf === 'function') return resolve()
        if (performance.now() - t0 > timeoutMs) return reject(new Error('html2pdf no cargó a tiempo'))
        setTimeout(tick, 50)
      }
      tick()
    })
  }

  _meta(id) {
    return (document.getElementById(id)?.getAttribute('content') || '').trim()
  }

  _getSavedResponseHtml() {
    const tpl = document.getElementById('pdf-body')
    if (tpl?.content) {
      return Array.from(tpl.content.childNodes)
        .map(n => n.outerHTML || n.textContent || '')
        .join('')
        .trim()
    }
    const jsonEl = document.getElementById('pdf-body-json')
    if (jsonEl) {
      try {
        return (JSON.parse(jsonEl.textContent || '{}').html || '').trim()
      } catch {}
    }
    return ''
  }

  _buildPdfHtml({ headerUrl, baseHref, complaintCode, innerHtml }) {
    return `
<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Respuesta #${this._esc(complaintCode)}</title>
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  html,body { margin:0; padding:0; font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; color:#111; }
  .container { max-width: 800px; margin: 24px auto 48px; padding: 0 16px; }
  .pdf-header { display:flex; justify-content:space-between; font-size:12px; margin-bottom:8px; gap:16px; }
  .pdf-header a { color: inherit; text-decoration: none; max-width: 60%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  hr { border:0; margin: 12px 0 24px; }
  .pdf-card { background:#fff; }
  .pdf-card .inner { padding: 24px; }
  .pdf-card .inner p { line-height: 1.55; margin: 0.6rem 0; }
  .pdf-card .inner .trix-content { line-height: 1.6; }
  img { max-width: 100%; height: auto; }
  .font-bold { font-weight: 700; }
  .mt-2 { margin-top: .5rem; }
  .mb-2 { margin-bottom: .5rem; }
</style>
<base href="${this._esc(baseHref)}">
</head>
<body>
  <div id="print-root" class="container">
    <div class="pdf-header">
      <div>Libro de Reclamaciones</div>
      <a href="${this._esc(headerUrl)}" target="_blank" rel="noopener noreferrer">${this._esc(headerUrl)}</a>
    </div>
    <hr>
    <div class="pdf-card"><div class="inner">
      ${innerHtml}
    </div></div>
  </div>
</body>
</html>
`
  }

  // Fallback: crear un DIV offscreen con <style> + body del builtHtml
  _makeOffscreenDivFromHtml(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html')
    const host = document.createElement('div')
    Object.assign(host.style, {
      position: 'fixed',
      left: '-9999px',
      top: '0',
      width: '1200px',
      minHeight: '1800px',
      visibility: 'hidden'
    })
    const styleEl = parsed.head.querySelector('style')
    if (styleEl) host.appendChild(styleEl.cloneNode(true))
    const bodyContent = parsed.body.querySelector('#print-root') || parsed.body
    host.appendChild(bodyContent.cloneNode(true))
    document.body.appendChild(host)
    return host
  }

  _enableSignButton(documentData, filename) {
    const container = document.getElementById('sign-button-container')
    if (!container) {
      console.warn('⚠️ No encontré #sign-button-container')
      return
    }
    container.classList.remove('hidden', 'opacity-50', 'pointer-events-none')
    container.style.removeProperty('display')
    container.querySelectorAll("button, [role='button'], a").forEach(el => {
      el.removeAttribute('disabled')
      el.classList.remove('cursor-not-allowed', 'opacity-50', 'pointer-events-none')
    })
    try {
      if (documentData?.id) container.dataset.documentId = documentData.id
      if (filename) container.dataset.filename = filename
    } catch {}
    container.dispatchEvent(
      new CustomEvent('pdf:uploaded', { bubbles: true, detail: { document: documentData, filename } })
    )
    const statusEl = container.querySelector('[data-sign-status]') || document.querySelector('[data-sign-status]')
    if (statusEl) statusEl.textContent = '✅ PDF enviado para firma'
    console.log('🟢 Botón de firma habilitado', { documentId: documentData?.id, filename })
  }

  _esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }
}
